/**
 * One Places row from a glance (Phase 11 plan §6.3). Plain TypeScript, like
 * the rest of `lib/forecast`: `PlacesSection` draws what this returns.
 */

import type { Glance } from '../api/types'
import { heroTemp } from './hero'
import { radarOverride, withRadar } from './now'
import { glanceTone, leadAlert, type Tone } from './tone'

export interface GlanceRow {
  /** null: no answer, so the block is untoned rather than calm. */
  tone: Tone | null
  /** "58°", or "—" with no observation. */
  temp: string
  /** The leading alert's event when one is active, else the sky. */
  line: string
  /** `line` is an alert's event. */
  alert: boolean
  /** "H 61°  L 39°", either end a dash; empty when both are missing. */
  highLow: string
}

const DASH = '—'

/**
 * The observation as the hero shows it: the radar's words in place of the
 * station's when the hero's rule says so (`radarOverride`), so a row never
 * says "Cloudy" over a Forecast section saying "Moderate rain". The glance
 * carries `now` for exactly this and for the widgets, which port it.
 */
export function glanceCurrent(glance: Pick<Glance, 'current' | 'now'>, nowMs: number): Glance['current'] {
  return withRadar(glance.current, radarOverride(glance.current, glance.now, nowMs))
}

export function glanceRow(glance: Glance | null, nowMs: number = Date.now()): GlanceRow {
  if (!glance) return { tone: null, temp: DASH, line: '', alert: false, highLow: '' }
  const lead = leadAlert(glance.alerts)
  const { high_f, low_f } = glance.today
  const current = glanceCurrent(glance, nowMs)
  return {
    tone: glanceTone({ current, alerts: glance.alerts }),
    temp: heroTemp(glance.current?.temperature_f),
    line: lead?.event ?? current?.description ?? '',
    alert: !!lead?.event,
    highLow: high_f === null && low_f === null ? '' : `H ${heroTemp(high_f)}  L ${heroTemp(low_f)}`,
  }
}
